import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, BookOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import AnnouncementBar from "@/components/AnnouncementBar";

const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const linkBase =
        "text-[11px] tracking-[0.22em] uppercase text-hl-text/80 hover:text-hl-secondary transition-colors duration-300";

    return (
        <header
            data-testid="main-navbar"
            className={`sticky top-0 z-40 transition-all duration-500 ${
                scrolled
                    ? "bg-hl-bg/95 backdrop-blur-md border-b border-hl-border/60"
                    : "bg-transparent"
            }`}
        >
            <AnnouncementBar />
            <div className={`max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between transition-all duration-500 ${scrolled ? "my-1" : "my-2"}`}>
                <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
                    <BookOpen className="w-4 h-4 text-hl-secondary" strokeWidth={1.5} />
                    <span className="font-serif text-2xl text-hl-brown tracking-tight">
                        Icook<span className="italic text-hl-secondary">Greek</span>
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    <NavLink to="/" data-testid="nav-home" end className={linkBase}>
                        Home
                    </NavLink>
                    <NavLink to="/recipes" data-testid="nav-recipes" className={linkBase}>
                        Recipes
                    </NavLink>
                    <NavLink to="/stories" data-testid="nav-stories" className={linkBase}>
                        Taste the Story
                    </NavLink>
                    <NavLink to="/aphrodites-roots" data-testid="nav-products" className={linkBase}>
                        Aphrodite's Roots
                    </NavLink>
                    <NavLink to="/our-story" data-testid="nav-our-story" className={linkBase}>
                        Our Story
                    </NavLink>
                    <NavLink to="/about" data-testid="nav-about" className={linkBase}>
                        About
                    </NavLink>
                    {user?.role === "admin" ? (
                        <Link
                            to="/admin"
                            data-testid="nav-admin"
                            className="text-[11px] tracking-[0.22em] uppercase px-4 py-2 rounded-full border border-hl-primary text-hl-primary hover:bg-hl-primary hover:text-hl-bg hl-btn-lift"
                        >
                            Admin
                        </Link>
                    ) : null}
                </nav>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className="md:hidden p-2 text-hl-brown"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open ? (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden bg-hl-bg border-t border-hl-border px-6 py-4 flex flex-col gap-3"
                >
                    {[
                        ["/", "Home"],
                        ["/recipes", "Recipes"],
                        ["/stories", "Taste the Story"],
                        ["/aphrodites-roots", "Aphrodite's Roots"],
                        ["/our-story", "Our Story"],
                        ["/about", "About"],
                    ].map(([to, label]) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={to === "/"}
                            onClick={() => setOpen(false)}
                            className="py-2 text-hl-text font-serif text-xl"
                        >
                            {label}
                        </NavLink>
                    ))}
                    {user?.role === "admin" ? (
                        <Link
                            to="/admin"
                            onClick={() => setOpen(false)}
                            className="py-2 text-hl-primary font-semibold"
                        >
                            Admin
                        </Link>
                    ) : null}
                </div>
            ) : null}
        </header>
    );
};

export default Navbar;
